import { authFlowText as text } from './auth-flow.text.js';
import type { AuthorizationRedirect, DeviceAuthorizationView } from '@aimo/contracts/web';
import { ApiClient } from './api.js';
import { errorMessage } from './design-canvas/error-copy.js';
import { element, button } from './dom.js';
import { locale } from './i18n.js';

export function deviceAuthorizationId(): string | undefined {
  if (location.pathname !== '/connect') return undefined;
  const request = new URLSearchParams(location.search).get('request');
  return request && /^[a-f0-9-]{36}$/.test(request) ? request : undefined;
}
export function loginReturnTo(): string { const id = deviceAuthorizationId(); return id ? '/connect?request=' + id : '/'; }
export function loginErrorText(): string {
  const error = new URLSearchParams(location.search).get('authError');
  if (!error) return '';
  if (error === 'google_cancelled') return text.google.cancelled;
  if (error === 'google_identity_denied') return text.google.denied;
  if (error === 'google_identity_conflict') return text.google.conflict;
  return text.google.expired;
}
export function googleLoginButton(api: ApiClient, onError: (message: string) => void): HTMLButtonElement {
  const control = button(text.google.button, () => {
    control.disabled = true; onError('');
    void api.request<AuthorizationRedirect>('googleLoginStart', { body: { returnTo: loginReturnTo() } }).then(result => {
      const url = new URL(result.authorizationUrl);
      if (url.origin !== 'https://accounts.google.com' || url.pathname !== '/o/oauth2/v2/auth') throw new Error(text.google.badUrl);
      location.assign(url.href);
    }).catch(error => { control.disabled = false; onError(error instanceof Error ? errorMessage(error) : text.google.unavailable); });
  }, 'button google-login');
  control.type = 'button'; return control;
}
export async function showDeviceAuthorization(api: ApiClient, mount: HTMLElement, requestId: string, switchAccount: () => Promise<void>): Promise<void> {
  const main = element('main', 'device-authorization'); const card = element('section', 'authorization-card');
  const heading = element('h1', '', text.heading); const status = element('p', 'muted', text.loading);
  status.setAttribute('role', 'status'); card.append(element('span', 'wordmark', 'AiMo'), heading, status); main.append(card); mount.replaceChildren(main);
  try {
    const request = await api.request<DeviceAuthorizationView>('deviceAuthorization', { params: { id: requestId } });
    status.textContent = request.status === 'pending' ? text.pending : text.processed;
    card.append(element('p', 'authorization-device', request.displayName), element('p', 'muted', text.expiresAt(new Date(request.expiresAt).toLocaleString(locale === 'zh' ? 'zh-CN' : 'en'))));
    card.append(element('p', 'authorization-account', text.account(api.session!.principal.displayName, api.session!.principal.accountName)), button(text.switchAccount, () => { void switchAccount(); }, 'button subtle'));
    if (request.status !== 'pending') return;
    const error = element('p', 'form-error'); error.setAttribute('role', 'alert');
    const actions = element('div', 'authorization-actions');
    const decide = async (decision: 'approve' | 'deny') => {
      approve.disabled = true; deny.disabled = true; error.textContent = '';
      try {
        const response = await api.request<AuthorizationRedirect>('deviceAuthorizationDecision', { params: { id: requestId }, body: { decision } });
        const url = new URL(response.authorizationUrl);
        if (url.protocol !== 'http:' || url.hostname !== '127.0.0.1' || !url.port || url.pathname !== '/aimo/callback' || url.username || url.password || url.hash) throw new Error(text.invalidReturn);
        status.textContent = decision === 'approve' ? text.approved : text.denied; actions.replaceChildren(); location.assign(url.href);
      } catch (failure) { error.textContent = failure instanceof Error ? errorMessage(failure) : text.incomplete; approve.disabled = false; deny.disabled = false; }
    };
    const approve = button(text.approve, () => { void decide('approve'); }, 'button primary');
    const deny = button(text.deny, () => { void decide('deny'); }, 'button subtle');
    actions.append(deny, approve); card.append(error, actions);
  } catch (error) { status.textContent = error instanceof Error ? errorMessage(error) : text.unavailable; }
}
